import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getVzLocationDealers, recordVzOwnership } from "@/lib/verizonSalesApi";

const NONE = "__none";

const Pick = ({ label, value, onChange, items, testid, placeholder }) => (
  <label className="block text-xs text-slate-600">
    {label}
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-9 mt-1 text-xs" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
    </Select>
  </label>
);

export const OwnershipChangeDialog = ({ open, onOpenChange, initial, stores, locations, dealers, onSaved }) => {
  const [f, setF] = useState({});
  const [assigned, setAssigned] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (open) setF({ ...initial }); }, [open, initial]);
  useEffect(() => {
    if (!f.location_id) { setAssigned([]); return; }
    getVzLocationDealers(f.location_id).then((d) => {
      setAssigned(d);
      setF((p) => ({ ...p, previous_dealer_ids: d.map((x) => x.id) }));
    }).catch(() => setAssigned([]));
  }, [f.location_id]);

  const tspItems = stores.map((s) => ({ value: s.door_tsp, label: `${s.store_id} · TSP ${s.door_tsp} · ${s.sub_agent_name || ""}` }));
  const prev = f.previous_dealer_ids || [];
  const toggle = (id) => set("previous_dealer_ids", prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    try {
      await recordVzOwnership({ ...f, new_dealer_id: f.new_dealer_id && f.new_dealer_id !== NONE ? f.new_dealer_id : null });
      toast.success("Ownership change saved");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the ownership change"); }
    setSaving(false);
  };

  const ready = f.location_id && f.old_tsp && f.new_tsp && f.effective_month && f.old_tsp !== f.new_tsp;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="vz-ownership-dialog">
        <DialogHeader>
          <DialogTitle>Ownership change / link another TSP</DialogTitle>
          <DialogDescription className="text-xs">
            Same Store ID, new door TSP. The old TSP's months before the change stay visible only to the previous owner and admins. The new owner sees the new TSP from the change month on.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Pick label="Portal location" value={f.location_id} onChange={(v) => set("location_id", v)} testid="vz-own-location"
            placeholder="Choose location…" items={locations.map((l) => ({ value: l.id, label: `${l.name} · ${l.city}` }))} />
          <div className="grid grid-cols-2 gap-3">
            <Pick label="Old door TSP (previous owner)" value={f.old_tsp} onChange={(v) => set("old_tsp", v)} testid="vz-own-old-tsp" placeholder="Old TSP…" items={tspItems} />
            <Pick label="New door TSP (new owner)" value={f.new_tsp} onChange={(v) => set("new_tsp", v)} testid="vz-own-new-tsp" placeholder="New TSP…" items={tspItems} />
          </div>
          <label className="block text-xs text-slate-600">
            First month under the new owner
            <Input type="month" className="h-9 mt-1 text-xs" value={f.effective_month || ""} onChange={(e) => set("effective_month", e.target.value)} data-testid="vz-own-month" />
          </label>
          <div className="text-xs text-slate-600">
            Previous owner(s): keep their history and are removed from this location
            <div className="mt-1 space-y-1" data-testid="vz-own-prev-dealers">
              {assigned.length === 0 && <p className="text-slate-400">No dealers are assigned to this location.</p>}
              {assigned.map((d) => (
                <label key={d.id} className="flex items-center gap-2">
                  <Checkbox checked={prev.includes(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`vz-own-prev-${d.id}`} />
                  <span>{d.name}</span>
                </label>
              ))}
            </div>
          </div>
          <Pick label="New owner dealer account (optional)" value={f.new_dealer_id} onChange={(v) => set("new_dealer_id", v)} testid="vz-own-new-dealer"
            placeholder="Assign later" items={[{ value: NONE, label: "Assign later" }, ...dealers.map((d) => ({ value: d.id, label: d.name }))]} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vz-own-cancel">Cancel</Button>
          <Button disabled={!ready || saving} onClick={save} data-testid="vz-own-save">{saving ? "Saving…" : "Save ownership change"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
